/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Box, IconButton, Switch, Typography } from '@evaneos/b2b-ui';
import AddOutlinedIcon from '@evaneos/b2b-ui/icons/AddOutlined';
import CloseOutlinedIcon from '@evaneos/b2b-ui/icons/CloseOutlined';

import {
    DAY_SPEC_IDS,
    timeToMinutes,
    validatePeriods,
} from '../../../resources/appointment/operations';
import type { DayKey, UIPeriod } from '../../../resources/appointment/operations';

import { AppointmentTimeSelect } from './AppointmentTimeStepper';

const SLOT_ERROR_LABELS: Record<string, string> = {
    undefined_start: 'Heure de début manquante',
    undefined_end: 'Heure de fin manquante',
    start_after_end: "L'heure de début doit être avant l'heure de fin",
    overlap_next: 'Ce créneau chevauche le créneau suivant',
};

type Props = {
    dayKey: DayKey;
    label: string;
    active: boolean;
    periods: UIPeriod[];
    onToggle: (active: boolean) => void;
    onSlotStartChange: (slotIdx: number, newStart: string) => void;
    onSlotEndChange: (slotIdx: number, newEnd: string) => void;
    onDeleteSlot: (slotIdx: number) => void;
    onAddSlot: () => void;
};

export const AppointmentDayRow = ({
    dayKey,
    label,
    active,
    periods,
    onToggle,
    onSlotStartChange,
    onSlotEndChange,
    onDeleteSlot,
    onAddSlot,
}: Props): JSX.Element => {
    const specId = DAY_SPEC_IDS[dayKey];
    const slotErrors = validatePeriods(periods);
    const lastEnd = periods[periods.length - 1]?.endTime;
    const canAddSlot = !lastEnd || timeToMinutes(lastEnd) < 23 * 60 + 30;

    return (
        <Box
            data-spec-id={`day-row-${specId}`}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, minHeight: 40 }}
        >
            {/* Day toggle */}
            <Switch
                data-spec-id={`day-toggle-${specId}`}
                checked={active}
                onChange={(e) => onToggle(e.target.checked)}
                size="small"
                sx={{ m: 0, flexShrink: 0 }}
            />

            {/* Day name — fixed width matches Figma 116px */}
            <Typography
                variant="body2"
                sx={{
                    fontWeight: active ? 700 : 400,
                    color: active ? 'text.primary' : 'text.secondary',
                    width: 116,
                    flexShrink: 0,
                    pt: 0.5,
                }}
            >
                {label}
            </Typography>

            {active && periods.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                    {periods.map((period: UIPeriod, slotIdx: number) => {
                        const errors = slotErrors[slotIdx] ?? [];
                        const startHasError = errors.some(
                            (e) => e === 'undefined_start' || e === 'start_after_end',
                        );
                        const endHasError = errors.some(
                            (e) =>
                                e === 'undefined_end' ||
                                e === 'start_after_end' ||
                                e === 'overlap_next',
                        );

                        const prevEnd = slotIdx > 0 ? periods[slotIdx - 1].endTime : undefined;
                        const startMinMinutes = prevEnd !== undefined ? timeToMinutes(prevEnd) : 0;
                        const endMinMinutes =
                            period.startTime !== undefined
                                ? timeToMinutes(period.startTime) + 15
                                : 0;

                        return (
                            <Box key={slotIdx}>
                                <Box
                                    data-spec-id={`slot-row-${specId}-${slotIdx}`}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <AppointmentTimeSelect
                                        data-spec-id={`slot-start-${specId}-${slotIdx}`}
                                        value={period.startTime}
                                        minMinutes={startMinMinutes}
                                        hasError={startHasError}
                                        onChange={(val) => onSlotStartChange(slotIdx, val)}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'text.primary', px: 0.25 }}
                                    >
                                        →
                                    </Typography>
                                    <AppointmentTimeSelect
                                        data-spec-id={`slot-end-${specId}-${slotIdx}`}
                                        value={period.endTime}
                                        minMinutes={endMinMinutes}
                                        hasError={endHasError}
                                        onChange={(val) => onSlotEndChange(slotIdx, val)}
                                    />
                                    <IconButton
                                        data-spec-id={`slot-delete-${specId}-${slotIdx}`}
                                        size="small"
                                        onClick={() => onDeleteSlot(slotIdx)}
                                        sx={{ color: 'text.secondary' }}
                                        aria-label="Supprimer ce créneau"
                                    >
                                        <CloseOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                {errors.length > 0 && (
                                    <Box sx={{ mt: 0.25 }}>
                                        {errors.map((err) => (
                                            <Typography
                                                key={err}
                                                variant="caption"
                                                sx={{ color: 'error.main', display: 'block' }}
                                            >
                                                {/* @todo translate: agenda.slot.error.{err} */}
                                                {SLOT_ERROR_LABELS[err]}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}

                    {periods.length < 10 && canAddSlot && (
                        <Box
                            data-spec-id={`slot-add-${specId}`}
                            component="button"
                            onClick={onAddSlot}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'primary.main',
                                fontSize: 14,
                                fontWeight: 600,
                                p: 0,
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            <AddOutlinedIcon fontSize="small" />
                            {/* @todo translate: agenda.day.add_slot */}
                            Ajouter créneau
                        </Box>
                    )}
                </Box>
            ) : (
                /* Inactive day or no periods */
                <Box
                    data-spec-id={`slot-add-${specId}`}
                    component="button"
                    onClick={() => {
                        if (!active) onToggle(true);
                        else onAddSlot();
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'text.secondary',
                        fontSize: 14,
                        p: 0,
                        pt: 0.5,
                        '&:hover': { color: 'primary.main' },
                    }}
                >
                    {/* @todo translate: agenda.day.add_slots */}
                    Ajouter des créneaux
                    <AddOutlinedIcon fontSize="small" />
                </Box>
            )}
        </Box>
    );
};
